import React, { useEffect, useState } from "react";
import AddLocalsView from "./AddLocalsView";

import { UserGreyIcon, LocationGreyIcon, CalendarGreyIcon, EditIcon, DeleteRedIcon, NewTabIcon, PlusThinWhiteIcon, FilterIcon } from "../../../assets/icons";
import { GetLocalResponse } from "../../../models/local/local.response";
import { localService } from "../../../services/localService";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { Switch } from "@mui/material";
import { confirmDialog } from "../../../utils/confirmDialog";
import EditLocalView from "./EditLocalView";
import AddLocalFromJsonView from "./AddLocalFromJsonView";


const LocalsPage: React.FC = () => {

    const [locals, setLocals] = useState<GetLocalResponse[]>([]);
    const [filterText, setFilterText] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);
    const [isAddLocalViewOpen, setIsAddLocalViewOpen] = useState(false);
    const [isEditLocalViewOpen, setIsEditLocalViewOpen] = useState(false);
    const [selectedLocalId, setSelectedLocalId] = useState<string | null>(null);
    const [isAddLocalFromJsonViewOpen, setIsAddLocalFromJsonViewOpen] = useState(false);

    const { notify } = useNotification();

    useEffect(() => {
        fetchLocals();
    }, []);

    const fetchLocals = async () => {
        setLoading(true);

        try {
            const response = await localService.getAll();

            if (response.success && response.data) {
                setLocals(response.data);
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to fetch locals.");
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSoftDelete = async (id: string) => {
        setLoading(true);

        try {
            const response = await localService.softDelete(id);

            if (response.success) {
                notify("success", response.message);
                fetchLocals();
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to delete local.");
            }
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteHard = async (id: string) => {

        const confirm = await confirmDialog({
            title: "Confirm Deletion",
            description: "Are you sure you want to permanently delete this local? This action cannot be undone.",
            isDelete: true
        });

        if (!confirm) {
            return;
        }

        setLoading(true);
        try {
            const response = await localService.hardDelete(id);
            if (response.success) {
                notify("success", response.message);
                fetchLocals();
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to delete local.");
            }
        } finally {
            setLoading(false);
        }
    }

    const handleRestore = async (id: string) => {
        setLoading(true);

        try {
            const response = await localService.restore(id);
            if (response.success) {
                notify("success", response.message);
                fetchLocals();
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to restore local.");
            }
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <>

            {isAddLocalViewOpen && (
                <AddLocalsView
                    onClose={() => {
                        setIsAddLocalViewOpen(false);
                        fetchLocals();
                    }} />
            )}

            {isEditLocalViewOpen && selectedLocalId && (
                <EditLocalView
                    id={selectedLocalId}
                    onClose={() => {
                        setIsEditLocalViewOpen(false);
                        setSelectedLocalId(null);
                        fetchLocals();
                    }}
                />
            )}

            {isAddLocalFromJsonViewOpen && (
                <AddLocalFromJsonView
                    onClose={() => { setIsAddLocalFromJsonViewOpen(false); }}
                />
            )}

            <section className="sa-locals-section">
                <div className="w-layout-blockcontainer container w-container">
                    <div className="sttings-content-wrapper">
                        <div className="header-div">
                            <div>
                                <h2 className="heading-3">Sve Lokacije</h2>
                                <div className="text-block">
                                    Pregled svih lokacija u sistemu<br />
                                </div>
                            </div>
                            <div className="header-button-div">
                                <div className="button-primary" style={{ marginRight: "10px" }} onClick={() => setIsAddLocalFromJsonViewOpen(true)}> 
                                    <img loading="lazy" src={PlusThinWhiteIcon} alt="" className="button-image" />
                                    <div className="button-text">Dodaj preko JSON</div>
                                </div>
                                <div className="button-primary" onClick={() => setIsAddLocalViewOpen(true)}>
                                    <img loading="lazy" src={PlusThinWhiteIcon} alt="" className="button-image" />
                                    <div className="button-text">Dodaj lokaciju</div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-settings-box-div table-block-div">
                            <div className="list-header">
                                <div className="list-header-info">
                                    <div className="list-header-title">Registrovane Lokacije</div>
                                    <div className="list-header-description">
                                        Ukupno {locals.length} lokacija ({locals.filter(local => !local.isDeleted).length} aktivnih)<br />
                                    </div>
                                </div>
                                <div className="list-header-filer">
                                    <img loading="lazy" src={FilterIcon} alt="" className="subcategory-header-filter-image" />
                                    <div className="list-header-filer-form w-form">
                                        <form className="form">
                                            <input className="form-input w-input" placeholder="Pretrazi lokacije..." type="text"
                                                value={filterText}
                                                onChange={(e) => setFilterText(e.target.value)}
                                            />
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <div className="list-div">
                                <div className="list-row-div list-header-row locals">
                                    <div>Lokacija</div>
                                    <div>Vlasnik</div>
                                    <div>Adresa</div>
                                    <div>Clanarina</div>
                                    <div>Kreiran</div>
                                    <div>Status</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076de3-1cee91cf">Akcije</div>
                                </div>

                                <div className="list-row-div locals">
                                    <div className="sa-locals-info-div">
                                        <div className="sa-local-photo-div">
                                            <img loading="lazy" src={"https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adfd_images.jpeg"} alt="" className="sa-local-photo" />
                                        </div>
                                        <div>
                                            <div className="sa-locals-local-name">Restoran Zlatna Lađa</div>
                                            <div className="description-text">info@zlatnalada.rs</div>
                                        </div>
                                    </div>
                                    <div className="sa-locals-local-user-div">
                                        <img loading="lazy" src={UserGreyIcon} alt="" className="icon-size-20" />
                                        <div>Petar Petrović</div>
                                    </div>
                                    <div className="sa-locals-local-location-div">
                                        <img loading="lazy" src={LocationGreyIcon} alt="" className="icon-size-20" />
                                        <div className="description-text">Knez Mihailova 15, Beograd</div>
                                    </div>
                                    <div className="small-box-div black-bg">
                                        <div className="sa-locals-local-plan-days-left">31 dana</div>
                                    </div>
                                    <div className="sa-locals-local-created-div">
                                        <img loading="lazy" src={CalendarGreyIcon} alt="" className="icon-size-20" />
                                        <div className="description-text">1. 2. 2025.</div>
                                    </div>
                                    <div>tgl-btn</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076dff-1cee91cf" className="table-row-action-btns-div">
                                        <img loading="lazy" src={NewTabIcon} alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src={EditIcon} alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src={DeleteRedIcon} alt="" className="category-list-div-row-actions-image" />
                                    </div>
                                </div>
                                <div className="list-row-div locals">
                                    <div className="sa-locals-info-div">
                                        <div className="sa-local-photo-div">
                                            <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adfd_images.jpeg" alt="" className="sa-local-photo" />
                                        </div>
                                        <div>
                                            <div className="sa-locals-local-name">Restoran Zlatna Lađa</div>
                                            <div className="description-text">info@zlatnalada.rs</div>
                                        </div>
                                    </div>
                                    <div className="sa-locals-local-user-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae03_user%20(5).png" alt="" className="icon-size-20" />
                                        <div>Petar Petrović</div>
                                    </div>
                                    <div className="sa-locals-local-location-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae06_location%20(2).png" alt="" className="icon-size-20" />
                                        <div className="description-text">Knez Mihailova 15, Beograd</div>
                                    </div>
                                    <div className="small-box-div grey-bg">
                                        <div className="sa-locals-local-plan-days-left">10 dana</div>
                                    </div>
                                    <div className="sa-locals-local-created-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae00_calendar%20(4).png" alt="" className="icon-size-20" />
                                        <div className="description-text">1. 2. 2025.</div>
                                    </div>
                                    <div>tgl-btn</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076e1d-1cee91cf" className="table-row-action-btns-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae09_new-tab.png" alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adaa_edit%20(3).png" alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adad_delete%20(1).png" alt="" className="category-list-div-row-actions-image" />
                                    </div>
                                </div>
                                <div className="list-row-div locals">
                                    <div className="sa-locals-info-div">
                                        <div className="sa-local-photo-div">
                                            <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adfd_images.jpeg" alt="" className="sa-local-photo" />
                                        </div>
                                        <div>
                                            <div className="sa-locals-local-name">Restoran Zlatna Lađa</div>
                                            <div className="description-text">info@zlatnalada.rs</div>
                                        </div>
                                    </div>
                                    <div className="sa-locals-local-user-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae03_user%20(5).png" alt="" className="icon-size-20" />
                                        <div>Petar Petrović</div>
                                    </div>
                                    <div className="sa-locals-local-location-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae06_location%20(2).png" alt="" className="icon-size-20" />
                                        <div className="description-text">Knez Mihailova 15, Beograd</div>
                                    </div>
                                    <div className="small-box-div red-bg">
                                        <div className="sa-locals-local-plan-days-left">5 dana</div>
                                    </div>
                                    <div className="sa-locals-local-created-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae00_calendar%20(4).png" alt="" className="icon-size-20" />
                                        <div className="description-text">1. 2. 2025.</div>
                                    </div>
                                    <div>tgl-btn</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076e3b-1cee91cf" className="table-row-action-btns-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae09_new-tab.png" alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adaa_edit%20(3).png" alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adad_delete%20(1).png" alt="" className="category-list-div-row-actions-image" />
                                    </div>
                                </div>
                                <div className="list-row-div locals">
                                    <div className="sa-locals-info-div">
                                        <div className="sa-local-photo-div">
                                            <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adfd_images.jpeg" alt="" className="sa-local-photo" />
                                        </div>
                                        <div>
                                            <div className="sa-locals-local-name">Restoran Zlatna Lađa</div>
                                            <div className="description-text">info@zlatnalada.rs</div>
                                        </div>
                                    </div>
                                    <div className="sa-locals-local-user-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae03_user%20(5).png" alt="" className="icon-size-20" />
                                        <div>Petar Petrović</div>
                                    </div>
                                    <div className="sa-locals-local-location-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae06_location%20(2).png" alt="" className="icon-size-20" />
                                        <div className="description-text">Knez Mihailova 15, Beograd</div>
                                    </div>
                                    <div className="small-box-div red-bg">
                                        <div className="sa-locals-local-plan-days-left">Istekla</div>
                                    </div>
                                    <div className="sa-locals-local-created-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae00_calendar%20(4).png" alt="" className="icon-size-20" />
                                        <div className="description-text">1. 2. 2025.</div>
                                    </div>
                                    <div>tgl-btn</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076e59-1cee91cf" className="table-row-action-btns-div">
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17ae09_new-tab.png" alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adaa_edit%20(3).png" alt="" className="category-list-div-row-actions-image" />
                                        <img loading="lazy" src="https://cdn.prod.website-files.com/6919e066813f7ba31cee91ac/6919e1cea5b1e8f2ca17adad_delete%20(1).png" alt="" className="category-list-div-row-actions-image" />
                                    </div>
                                </div>

                                {locals
                                    .filter(local =>
                                        local.name.toLowerCase().includes(filterText.toLowerCase()) ||
                                        local.email.toLowerCase().includes(filterText.toLowerCase()) ||
                                        local.ownerFullName.toLowerCase().includes(filterText.toLowerCase()) ||
                                        local.address.toLowerCase().includes(filterText.toLowerCase()))
                                    .map((local) => (
                                        <div className="list-row-div locals">
                                            <div className="sa-locals-info-div">
                                                <div className="sa-local-photo-div">
                                                    <img loading="lazy" src={local.logoImageUrl + "@ul.webp"} alt={`local-logo-${local.id}`} className="sa-local-photo" />
                                                </div>
                                                <div>
                                                    <div className="sa-locals-local-name">{local.name}</div>
                                                    <div className="description-text">{local.email}</div>
                                                </div>
                                            </div>
                                            <div className="sa-locals-local-user-div">
                                                <img loading="lazy" src={UserGreyIcon} alt="" className="icon-size-20" />
                                                <div>{local.ownerFullName}</div>
                                            </div>
                                            <div className="sa-locals-local-location-div">
                                                <img loading="lazy" src={LocationGreyIcon} alt="" className="icon-size-20" />
                                                <div className="description-text">{local.address}</div>
                                            </div>
                                            <div className="small-box-div black-bg">
                                                <div className="sa-locals-local-plan-days-left">{local.planEndAt ? ((new Date(local.planEndAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)).toFixed(0) : ''} dana</div>
                                            </div>
                                            <div className="sa-locals-local-created-div">
                                                <img loading="lazy" src={CalendarGreyIcon} alt="" className="icon-size-20" />
                                                <div className="description-text">{new Date(local.createdAt).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                                            </div>
                                            <Switch
                                                checked={local.isDeleted ? false : true}
                                                onChange={() => { local.isDeleted ? handleRestore(local.id) : handleSoftDelete(local.id); }}
                                            />
                                            <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076dff-1cee91cf" className="table-row-action-btns-div">
                                                <img loading="lazy" src={NewTabIcon} className="category-list-div-row-actions-image" onClick={() => window.open("https://app.quickmenu.rs/client-menu/" + local.id + "/online", '_blank')} />
                                                <img loading="lazy" src={EditIcon} alt="" className="category-list-div-row-actions-image"
                                                    onClick={() => {
                                                        setSelectedLocalId(local.id);
                                                        setIsEditLocalViewOpen(true);
                                                    }}
                                                />
                                                <img loading="lazy" src={DeleteRedIcon} alt="" className="category-list-div-row-actions-image" onClick={() => handleDeleteHard(local.id)} />
                                            </div>
                                        </div>
                                    ))}

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default LocalsPage;